import React, { useState } from 'react';
import { useAdminProjects } from '../hooks/useProjects';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Plus,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  Copy,
  ArrowLeft,
  RefreshCcw,
  AlertTriangle,
  ShieldCheck,
  Lock,
} from 'lucide-react';

const apiFetch = (path, init = {}) =>
  fetch(path, { credentials: 'same-origin', ...init });

const AdminProjects = () => {
  const navigate = useNavigate();
  const { allProjects, loading, error, refresh } = useAdminProjects();
  const [busyId, setBusyId] = useState(null);

  const setHidden = async (p, hidden) => {
    setBusyId(p.id);
    try {
      const res = await apiFetch(`/api/admin/projects/${encodeURIComponent(p.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(hidden ? 'Project hidden' : 'Project visible');
      refresh();
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete "${p.title.en || p.title.de}"? This cannot be undone.`)) return;
    setBusyId(p.id);
    try {
      const res = await apiFetch(`/api/admin/projects/${encodeURIComponent(p.id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Project deleted');
      refresh();
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const cloneProject = (p) => {
    // Pass full project as state; the form pre-fills, lets you edit, then POSTs as new.
    navigate('/admin/projects/new', { state: { cloneFrom: p } });
  };

  if (error === 'unauthorized') {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#D95871]/15 mb-6">
          <AlertTriangle className="w-6 h-6 text-[#D95871]" />
        </div>
        <h1 className="text-3xl font-bold text-slate-50 mb-3">Access required</h1>
        <p className="text-slate-400">Sign in via Cloudflare Access to manage projects.</p>
      </div>
    );
  }

  return (
    <div>
      <section className="relative border-b border-white/10 sky-section overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] cfd-bar" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4A8FD1] mb-3">
                <ShieldCheck className="w-4 h-4" />
                <span className="w-8 h-px bg-[#4A8FD1]" />
                <span>Admin</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
                Projects
              </h1>
              <p className="mt-2 text-sm text-slate-400">
                {allProjects.length} total · {allProjects.filter((p) => p.hidden).length} hidden
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-white/15 text-slate-300 hover:bg-white/5"
              >
                <ArrowLeft className="w-4 h-4" />
                Inbox
              </Link>
              <Button
                variant="outline"
                className="border-white/15 bg-transparent text-slate-200 hover:bg-white/5"
                onClick={refresh}
                disabled={loading}
              >
                <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Link
                to="/admin/projects/new"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-[#4A8FD1] text-slate-50 hover:bg-[#6AAFE0]"
                data-testid="admin-new-project-btn"
              >
                <Plus className="w-4 h-4" />
                New project
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10">
        {loading && allProjects.length === 0 ? (
          <div className="p-8 rounded-lg border border-white/10 bg-[#1A2231] text-slate-400">
            Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {allProjects.map((p) => {
              const isMock = p.source === 'mock';
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    p.hidden
                      ? 'border-white/5 bg-[#10151F] opacity-60'
                      : 'border-white/10 bg-[#1A2231]'
                  }`}
                  data-testid={`admin-project-row-${p.id}`}
                >
                  <div className="w-20 h-14 rounded bg-white/10 overflow-hidden flex-shrink-0">
                    {p.thumbnail && (
                      <img src={p.thumbnail} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-50 truncate">
                        {p.title.en || p.title.de || p.id}
                      </span>
                      {isMock && (
                        <Badge variant="secondary" className="bg-white/10 text-slate-300 text-[10px] mono uppercase">
                          <Lock className="w-3 h-3 mr-1" />
                          mock
                        </Badge>
                      )}
                      {p.hidden && (
                        <Badge variant="secondary" className="bg-[#D95871]/20 text-[#FFB3BF] text-[10px] mono uppercase">
                          hidden
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mono mt-0.5 truncate">
                      {p.year} · {p.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/15 bg-transparent text-slate-200 hover:bg-white/5"
                      onClick={() => setHidden(p, !p.hidden)}
                      disabled={busyId === p.id}
                      title={p.hidden ? 'Show on site' : 'Hide from site'}
                    >
                      {p.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/15 bg-transparent text-slate-200 hover:bg-white/5"
                      onClick={() => cloneProject(p)}
                      title="Clone"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    {!isMock && (
                      <Link
                        to={`/admin/projects/${encodeURIComponent(p.id)}/edit`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-white/15 text-slate-200 hover:bg-white/5"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </Link>
                    )}
                    {!isMock && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#D95871]/30 bg-transparent text-[#FFB3BF] hover:bg-[#D95871]/10"
                        onClick={() => deleteProject(p)}
                        disabled={busyId === p.id}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProjects;
